/**
 * /fleet/:vehicleId — detalle con 7 pestañas (contenido inicial + empty states).
 */
import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { ArrowLeft, Link2, Pencil, Trash2, Truck } from 'lucide-react'
import { PageHeader } from '@/components/app-shell'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { KpiCard } from '@/components/ui/kpi-card'
import { StatusPill } from '@/components/ui/status-pill'
import {
  ApiError,
  createOdometerReading,
  createVehicleComponent,
  deleteMaintenance,
  deleteVehicleComponent,
  patchMaintenance,
  patchVehicleComponent,
  deleteOdometerReading,
  patchOdometerReading,
  type OdometerReading,
  getVehicle,
  listExpenses,
  listInspections,
  listMaintenance,
  listOdometerHistory,
  listVehicleComponents,
  listWorkOrders,
} from '@/lib/api'
import { uploadDocument } from '@/lib/upload'
import { RowActions } from '@/components/row-actions'
import { cn } from '@/lib/utils'

const TABS = [
  'Overview',
  'Inspections',
  'Work orders',
  'Maintenance',
  'Components',
  'Odometer',
  'Expenses',
] as const

type Tab = (typeof TABS)[number]

export function FleetDetailPage() {
  const { vehicleId } = useParams({ from: '/fleet/$vehicleId' })
  const [tab, setTab] = useState<Tab>('Overview')

  const query = useQuery({
    queryKey: ['vehicle', vehicleId],
    queryFn: () => getVehicle(vehicleId),
  })

  const qc = useQueryClient()
  const photoInputRef = useRef<HTMLInputElement>(null)
  const [photoError, setPhotoError] = useState<string | null>(null)

  // El vehículo ya existe, así que la foto se sube en cuanto se elige: no hay
  // formulario que enviar ni nada que esperar.
  const photoMutation = useMutation({
    mutationFn: (file: File) =>
      uploadDocument({
        file,
        attachableType: 'vehicle',
        attachableId: vehicleId,
        field: 'primary_photo_url',
        description: 'Foto principal del vehículo',
      }),
    onSuccess: async () => {
      setPhotoError(null)
      // Refresca la ficha para que aparezca la foto recién subida.
      await qc.invalidateQueries({ queryKey: ['vehicle', vehicleId] })
      await qc.invalidateQueries({ queryKey: ['vehicles'] })
    },
    onError: (err) => {
      setPhotoError(err instanceof ApiError ? err.message : 'No se pudo subir la foto.')
    },
  })

  const inspectionsQ = useQuery({
    queryKey: ['inspections', { vehicleId }],
    queryFn: () => listInspections({ vehicleId }),
    enabled: tab === 'Inspections',
  })

  const workOrdersQ = useQuery({
    queryKey: ['work-orders', { vehicleId }],
    queryFn: () => listWorkOrders({ vehicleId }),
    enabled: tab === 'Work orders',
  })

  const maintenanceQ = useQuery({
    queryKey: ['maintenance', { vehicleId }],
    queryFn: () => listMaintenance({ vehicleId }),
    enabled: tab === 'Maintenance',
  })

  const componentsQ = useQuery({
    queryKey: ['components', vehicleId],
    queryFn: () => listVehicleComponents(vehicleId),
    enabled: tab === 'Components',
  })

  const odometerQ = useQuery({
    queryKey: ['odometer', vehicleId],
    queryFn: () => listOdometerHistory(vehicleId),
    enabled: tab === 'Odometer',
  })

  const expensesQ = useQuery({
    queryKey: ['expenses', { vehicleId }],
    queryFn: () => listExpenses({ vehicleId }),
    enabled: tab === 'Expenses',
  })

  const v = query.data?.vehicle

  if (query.isLoading) {
    return <p className="text-sm text-muted-foreground">Cargando unidad…</p>
  }

  if (query.isError || !v) {
    return (
      <Card>
        <EmptyState
          title="Vehículo no encontrado"
          description={(query.error as Error | null)?.message ?? 'Revisa el enlace.'}
          action={
            <Link to="/fleet" className="text-sm font-medium text-primary">
              Volver a Fleet
            </Link>
          }
        />
      </Card>
    )
  }

  const title = [v.year, v.make, v.model].filter(Boolean).join(' ') || v.unitNumber

  return (
    <>
      <div className="mb-3">
        <Link
          to="/fleet"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Fleet
        </Link>
      </div>

      <PageHeader
        title={v.unitNumber}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-2">
            <span>{title}</span>
            <StatusPill status={v.status} />
            <span className="text-muted-foreground">· {v.vehicleType}</span>
          </span>
        }
        actions={
          <Link
            to="/fleet/$vehicleId/edit"
            params={{ vehicleId }}
            className="inline-flex min-h-11 items-center gap-1.5 rounded-lg border border-border bg-card px-3 text-sm font-medium hover:bg-muted"
          >
            <Pencil className="size-4" aria-hidden />
            Editar
          </Link>
        }
      />

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          label="Odómetro"
          value={v.currentOdometer.toLocaleString()}
          hint={v.odometerUnit}
        />
        <KpiCard label="Año" value={v.year ?? '—'} />
        <KpiCard label="Placa" value={v.licensePlate ?? '—'} />
        <KpiCard
          label="Adquisición"
          value={
            v.acquisitionPrice != null
              ? `$${v.acquisitionPrice.toLocaleString()}`
              : '—'
          }
        />
      </div>

      <div
        role="tablist"
        aria-label="Secciones del vehículo"
        className="no-print mb-4 flex gap-1 overflow-x-auto border-b border-border"
      >
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={cn(
              'shrink-0 cursor-pointer border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
              tab === t
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Overview' ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader title="Identificación" />
            <CardBody className="space-y-2 text-sm">
              <Row label="VIN" value={v.vin ?? '—'} />
              <Row label="Color" value={v.color ?? '—'} />
              <Row label="SunPass" value={v.sunpassNumber ?? '—'} />
              <Row label="Notas" value={v.notes ?? '—'} />
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Foto" />
            <CardBody className="space-y-3">
              {v.primaryPhotoUrl ? (
                <img
                  src={v.primaryPhotoUrl}
                  alt={`Vehículo ${v.unitNumber}`}
                  className="max-h-64 w-full rounded-xl object-cover"
                  // Las rutas que trae el Excel histórico apuntan a un almacén
                  // que no es este; si no cargan, se oculta la imagen rota en
                  // vez de dejar el icono de fallo.
                  onError={(e) => {
                    ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                  }}
                />
              ) : (
                <EmptyState
                  className="py-8"
                  title="Sin foto"
                  description="Haz una foto o elige una imagen para adjuntarla a este vehículo."
                  icon={Truck}
                />
              )}

              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="sr-only"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  // El value se limpia para que elegir DOS VECES la misma foto
                  // vuelva a disparar el change (si no, el navegador lo ignora).
                  e.target.value = ''
                  if (file) photoMutation.mutate(file)
                }}
              />
              <Button
                variant="secondary"
                className="w-full"
                loading={photoMutation.isPending}
                onClick={() => photoInputRef.current?.click()}
              >
                {v.primaryPhotoUrl ? 'Cambiar foto' : 'Añadir foto'}
              </Button>
              {photoError && (
                <p role="alert" className="text-sm font-medium text-danger">
                  {photoError}
                </p>
              )}
            </CardBody>
          </Card>
        </div>
      ) : tab === 'Inspections' ? (
        <Card className="divide-y divide-border overflow-hidden">
          {inspectionsQ.isLoading ? (
            <p className="p-4 text-sm text-muted-foreground">Cargando…</p>
          ) : (inspectionsQ.data?.inspections ?? []).length === 0 ? (
            <EmptyState title="Sin inspecciones" description="Esta unidad aún no tiene revisiones." />
          ) : (
            (inspectionsQ.data?.inspections ?? []).map((row) => (
              <Link
                key={row.id}
                to="/inspections/$inspectionId"
                params={{ inspectionId: row.id }}
                className="flex min-h-[56px] items-center justify-between gap-3 px-4 py-3 hover:bg-muted"
              >
                <span className="text-sm">
                  {row.inspectionType} · {new Date(row.inspectionDatetime).toLocaleDateString()}
                </span>
                <StatusPill status={row.overallStatus} />
              </Link>
            ))
          )}
        </Card>
      ) : tab === 'Work orders' ? (
        <Card className="divide-y divide-border overflow-hidden">
          {workOrdersQ.isLoading ? (
            <p className="p-4 text-sm text-muted-foreground">Cargando…</p>
          ) : (workOrdersQ.data?.workOrders ?? []).length === 0 ? (
            <EmptyState title="Sin órdenes" description="No hay reparaciones ligadas a esta unidad." />
          ) : (
            (workOrdersQ.data?.workOrders ?? []).map((wo) => (
              <Link
                key={wo.id}
                to="/work-orders/$workOrderId"
                params={{ workOrderId: wo.id }}
                className="flex min-h-[56px] items-center justify-between gap-3 px-4 py-3 hover:bg-muted"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">{wo.title}</span>
                  <span className="text-xs text-muted-foreground">{wo.workOrderNumber}</span>
                </span>
                <StatusPill status={wo.status} />
              </Link>
            ))
          )}
        </Card>
      ) : tab === 'Maintenance' ? (
        <Card className="divide-y divide-border overflow-hidden">
          {(maintenanceQ.data?.schedules ?? []).length === 0 ? (
            <EmptyState title="Sin programas" description="No hay mantenimiento preventivo." />
          ) : (
            (maintenanceQ.data?.schedules ?? []).map((r) => (
              <MaintenanceRow key={r.id} vehicleId={vehicleId} schedule={r} />
            ))
          )}
        </Card>
      ) : tab === 'Components' ? (
        <Card className="divide-y divide-border overflow-hidden">
          <ComponentQuickAdd vehicleId={vehicleId} />
          {(componentsQ.data?.components ?? []).length === 0 ? (
            <EmptyState title="Sin componentes" description="Baterías, llantas, etc." />
          ) : (
            (componentsQ.data?.components ?? []).map((c) => (
              <ComponentRow key={c.id} vehicleId={vehicleId} component={c} />
            ))
          )}
        </Card>
      ) : tab === 'Odometer' ? (
        <Card className="divide-y divide-border overflow-hidden">
          <OdometerQuickAdd vehicleId={vehicleId} />
          {(odometerQ.data?.readings ?? []).length === 0 ? (
            <EmptyState title="Sin lecturas" />
          ) : (
            (odometerQ.data?.readings ?? []).map((r) => (
              <OdometerRow key={r.id} vehicleId={vehicleId} reading={r} />
            ))
          )}
        </Card>
      ) : tab === 'Expenses' ? (
        <Card className="divide-y divide-border overflow-hidden">
          {(expensesQ.data?.expenses ?? []).length === 0 ? (
            <EmptyState title="Sin gastos" />
          ) : (
            (expensesQ.data?.expenses ?? []).map((e) => (
              <div key={e.id} className="flex justify-between px-4 py-3 text-sm">
                <span>
                  {e.date} · {e.description || e.categoryName || 'Gasto'}
                </span>
                <span className="font-medium">${e.amount.toLocaleString()}</span>
              </div>
            ))
          )}
        </Card>
      ) : (
        <Card>
          <EmptyState title={`${tab}`} description="Sin contenido." />
        </Card>
      )}
    </>
  )
}

/** Fecha de hoy en formato YYYY-MM-DD y en hora LOCAL. */
function todayISO(): string {
  const now = new Date()
  // toISOString() daría UTC: de madrugada en horario de verano eso adelanta la
  // fecha un día y la lectura se guardaría con fecha de mañana.
  const offset = now.getTimezoneOffset() * 60_000
  return new Date(now.getTime() - offset).toISOString().slice(0, 10)
}

/**
 * Alta de lectura.
 *
 * La fecha es elegible y no fija a hoy: las lecturas se apuntan en papel en el
 * patio y se pasan al sistema más tarde —a veces al día siguiente—, y forzar la
 * fecha de hoy metía el dato en el día equivocado, con lo que el historial
 * dejaba de servir para calcular millas por día.
 */
function OdometerQuickAdd({ vehicleId }: { vehicleId: string }) {
  const [reading, setReading] = useState('')
  const [date, setDate] = useState(todayISO())
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()

  const add = useMutation({
    mutationFn: () =>
      createOdometerReading({
        vehicleId,
        reading: Number(reading),
        date,
        notes: notes || null,
      }),
    onSuccess: async () => {
      setReading('')
      setNotes('')
      setDate(todayISO())
      setError(null)
      await qc.invalidateQueries({ queryKey: ['odometer', vehicleId] })
      await qc.invalidateQueries({ queryKey: ['vehicle', vehicleId] })
      await qc.invalidateQueries({ queryKey: ['vehicles'] })
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'No se pudo guardar.'),
  })

  return (
    <div className="space-y-2 border-b border-border px-4 py-3">
      <div className="flex flex-wrap gap-2">
        <input
          type="number"
          inputMode="decimal"
          placeholder="Lectura"
          value={reading}
          onChange={(e) => setReading(e.target.value)}
          className="h-11 min-w-0 flex-1 rounded-lg border border-input bg-card px-3 text-sm"
          aria-label="Lectura"
        />
        <input
          type="date"
          value={date}
          max={todayISO()}
          onChange={(e) => setDate(e.target.value)}
          className="h-11 rounded-lg border border-input bg-card px-3 text-sm"
          aria-label="Fecha de la lectura"
        />
        <Button
          type="button"
          loading={add.isPending}
          disabled={!reading}
          onClick={() => add.mutate()}
        >
          Guardar
        </Button>
      </div>
      <input
        type="text"
        placeholder="Nota (opcional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        className="h-11 w-full rounded-lg border border-input bg-card px-3 text-sm"
        aria-label="Nota"
      />
      {error && (
        <p role="alert" className="text-xs font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium break-all">{value}</span>
    </div>
  )
}

/**
 * Una lectura de odómetro, editable en el sitio.
 *
 * Editar y borrar existen porque el error típico es de dedo —480000 por
 * 48000— y hasta ahora solo se podían añadir lecturas: la equivocada se
 * quedaba en el historial y, peor, dejaba `vehicles.current_odometer` inflado
 * para siempre (el trigger del esquema solo sube, y solo al insertar).
 *
 * El servidor recalcula ese campo en cada cambio y devuelve cómo queda, así
 * que aquí se refresca también la ficha del vehículo y se avisa si el número ya
 * no lo respalda ninguna lectura.
 */
function OdometerRow({
  vehicleId,
  reading,
}: {
  vehicleId: string
  reading: OdometerReading
}) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(String(reading.reading))
  const [date, setDate] = useState(reading.date)
  const [error, setError] = useState<string | null>(null)
  const [warning, setWarning] = useState<string | null>(null)

  async function refresh() {
    await qc.invalidateQueries({ queryKey: ['odometer', vehicleId] })
    // La ficha muestra current_odometer, que el servidor acaba de recalcular.
    await qc.invalidateQueries({ queryKey: ['vehicle', vehicleId] })
    await qc.invalidateQueries({ queryKey: ['vehicles'] })
  }

  const save = useMutation({
    mutationFn: () =>
      patchOdometerReading(reading.id, { reading: Number(value), date }),
    onSuccess: async (res) => {
      setEditing(false)
      setError(null)
      setWarning(
        res.vehicle.hasReadings
          ? null
          : 'Era la última lectura del vehículo: su odómetro se mantiene sin respaldo. Corrígelo en Editar si no es correcto.',
      )
      await refresh()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'No se pudo guardar.'),
  })

  const remove = useMutation({
    mutationFn: () => deleteOdometerReading(reading.id),
    onSuccess: async (res) => {
      setError(null)
      if (!res.vehicle.hasReadings) {
        setWarning(
          'Era la última lectura: el odómetro del vehículo se queda como estaba. Corrígelo en Editar si hace falta.',
        )
      }
      await refresh()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'No se pudo eliminar.'),
  })

  if (editing) {
    return (
      <div className="space-y-2 px-4 py-3">
        <div className="flex flex-wrap gap-2">
          <input
            type="number"
            inputMode="decimal"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="h-11 min-w-0 flex-1 rounded-lg border border-input bg-card px-3 text-sm"
            aria-label="Lectura"
          />
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-11 rounded-lg border border-input bg-card px-3 text-sm"
            aria-label="Fecha"
          />
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            className="flex-1"
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Guardar
          </Button>
          <Button type="button" variant="outline" onClick={() => setEditing(false)}>
            Cancelar
          </Button>
        </div>
        {error && (
          <p role="alert" className="text-xs font-medium text-danger">
            {error}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="px-4 py-3 text-sm">
      <div className="flex items-center justify-between gap-3">
        <span className="min-w-0">
          <span className="inline-flex items-center gap-1.5">
            {reading.date} · {reading.source}
            {/* Cadena = la lectura no es suelta, la generó otro registro. Editarla
                aquí corrige también ese registro (el servidor sincroniza los dos),
                y por eso conviene que se vea de un vistazo. */}
            {reading.source !== 'Manual' &&
              (reading.source === 'Inspection' && reading.sourceId ? (
                <Link
                  to="/inspections/$inspectionId"
                  params={{ inspectionId: reading.sourceId }}
                  className="inline-flex items-center text-muted-foreground hover:text-foreground"
                  title="Lectura enlazada con una inspección — abrir"
                  aria-label="Ver la inspección que originó esta lectura"
                >
                  <Link2 className="size-3.5" aria-hidden />
                </Link>
              ) : (
                <Link2
                  className="size-3.5 text-muted-foreground"
                  aria-label={`Lectura enlazada con: ${reading.source}`}
                />
              ))}
          </span>
          {reading.notes ? (
            <span className="block truncate text-xs text-muted-foreground">{reading.notes}</span>
          ) : null}
        </span>
        <span className="flex items-center gap-2">
          <span className="font-medium">{reading.reading.toLocaleString()}</span>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex size-9 cursor-pointer items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={`Editar lectura del ${reading.date}`}
          >
            <Pencil className="size-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => remove.mutate()}
            disabled={remove.isPending}
            className="inline-flex size-9 cursor-pointer items-center justify-center rounded-lg text-muted-foreground hover:bg-danger-muted hover:text-danger disabled:opacity-50"
            aria-label={`Eliminar lectura del ${reading.date}`}
          >
            <Trash2 className="size-4" aria-hidden />
          </button>
        </span>
      </div>
      {error && (
        <p role="alert" className="mt-1 text-xs font-medium text-danger">
          {error}
        </p>
      )}
      {warning && <p className="mt-1 text-xs text-warning-foreground">{warning}</p>}
    </div>
  )
}

/** Alta rápida de componente: tipo y posición son lo mínimo para identificarlo. */
function ComponentQuickAdd({ vehicleId }: { vehicleId: string }) {
  const qc = useQueryClient()
  const [type, setType] = useState('')
  const [position, setPosition] = useState('')
  const [serial, setSerial] = useState('')
  const [error, setError] = useState<string | null>(null)

  const add = useMutation({
    mutationFn: () =>
      createVehicleComponent({
        vehicleId,
        componentType: type,
        position: position || null,
        serialNumber: serial || null,
      }),
    onSuccess: async () => {
      setType('')
      setPosition('')
      setSerial('')
      setError(null)
      await qc.invalidateQueries({ queryKey: ['components', vehicleId] })
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'No se pudo guardar.'),
  })

  return (
    <div className="space-y-2 border-b border-border px-4 py-3">
      <div className="flex flex-wrap gap-2">
        <input
          value={type}
          onChange={(e) => setType(e.target.value)}
          placeholder="Tipo (Batería, Llanta…)"
          className="h-11 min-w-0 flex-1 rounded-lg border border-input bg-card px-3 text-sm"
          aria-label="Tipo de componente"
        />
        <input
          value={position}
          onChange={(e) => setPosition(e.target.value)}
          placeholder="Posición"
          className="h-11 w-32 rounded-lg border border-input bg-card px-3 text-sm"
          aria-label="Posición"
        />
        <input
          value={serial}
          onChange={(e) => setSerial(e.target.value)}
          placeholder="Nº de serie"
          className="h-11 w-40 rounded-lg border border-input bg-card px-3 text-sm"
          aria-label="Número de serie"
        />
        <Button type="button" loading={add.isPending} disabled={!type} onClick={() => add.mutate()}>
          Añadir
        </Button>
      </div>
      {error && (
        <p role="alert" className="text-xs font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  )
}

/** Un componente instalado, corregible en el sitio. */
function ComponentRow({
  vehicleId,
  component,
}: {
  vehicleId: string
  component: {
    id: string
    componentType: string
    name: string | null
    position: string | null
    serialNumber: string | null
    installationDate: string | null
  }
}) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [type, setType] = useState(component.componentType)
  const [position, setPosition] = useState(component.position ?? '')
  const [serial, setSerial] = useState(component.serialNumber ?? '')
  const [error, setError] = useState<string | null>(null)

  const refresh = () => qc.invalidateQueries({ queryKey: ['components', vehicleId] })

  const save = useMutation({
    mutationFn: () =>
      patchVehicleComponent(component.id, {
        componentType: type,
        position: position || null,
        serialNumber: serial || null,
      }),
    onSuccess: async () => {
      setEditing(false)
      setError(null)
      await refresh()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'No se pudo guardar.'),
  })

  const remove = useMutation({
    mutationFn: () => deleteVehicleComponent(component.id),
    onSuccess: refresh,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'No se pudo eliminar.'),
  })

  if (editing) {
    return (
      <div className="space-y-2 px-4 py-3">
        <div className="flex flex-wrap gap-2">
          <input
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="h-11 min-w-0 flex-1 rounded-lg border border-input bg-card px-3 text-sm"
            aria-label="Tipo"
          />
          <input
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            className="h-11 w-32 rounded-lg border border-input bg-card px-3 text-sm"
            aria-label="Posición"
          />
          <input
            value={serial}
            onChange={(e) => setSerial(e.target.value)}
            className="h-11 w-40 rounded-lg border border-input bg-card px-3 text-sm"
            aria-label="Número de serie"
          />
        </div>
        <div className="flex gap-2">
          <Button type="button" className="flex-1" loading={save.isPending} onClick={() => save.mutate()}>
            Guardar
          </Button>
          <Button type="button" variant="outline" onClick={() => setEditing(false)}>
            Cancelar
          </Button>
        </div>
        {error && (
          <p role="alert" className="text-xs font-medium text-danger">
            {error}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="px-4 py-3 text-sm">
      <div className="flex items-center justify-between gap-3">
        <span className="min-w-0">
          <span className="block font-medium">
            {component.componentType}
            {component.position ? ` · ${component.position}` : ''}
          </span>
          <span className="block text-xs text-muted-foreground">
            {[component.name, component.serialNumber, component.installationDate]
              .filter(Boolean)
              .join(' · ') || 'Sin detalles'}
          </span>
        </span>
        <RowActions
          label={`el componente ${component.componentType}`}
          deleting={remove.isPending}
          onEdit={() => setEditing(true)}
          onDelete={() => remove.mutate()}
        />
      </div>
      {error && (
        <p role="alert" className="mt-1 text-xs font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  )
}

/**
 * Un plan de mantenimiento, corregible en el sitio.
 *
 * El intervalo y el próximo vencimiento son los que deciden qué sale como
 * "vencido" en el panel (v_maintenance_due). Corregirlos era imposible: solo
 * quedaba crear otro plan encima, y entonces el vehículo aparecía avisado dos
 * veces por lo mismo.
 */
function MaintenanceRow({
  vehicleId,
  schedule,
}: {
  vehicleId: string
  schedule: {
    id: string
    maintenanceName: string
    dueState: string
    nextDueDate: string | null
    nextDueOdometer: number | null
  }
}) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(schedule.maintenanceName)
  const [dueDate, setDueDate] = useState(schedule.nextDueDate ?? '')
  const [dueOdo, setDueOdo] = useState(
    schedule.nextDueOdometer != null ? String(schedule.nextDueOdometer) : '',
  )
  const [error, setError] = useState<string | null>(null)

  async function refresh() {
    await qc.invalidateQueries({ queryKey: ['maintenance', { vehicleId }] })
    await qc.invalidateQueries({ queryKey: ['maintenance'] })
  }

  const save = useMutation({
    mutationFn: () =>
      patchMaintenance(schedule.id, {
        maintenanceName: name,
        nextDueDate: dueDate || null,
        nextDueOdometer: dueOdo ? Number(dueOdo) : null,
      }),
    onSuccess: async () => {
      setEditing(false)
      setError(null)
      await refresh()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'No se pudo guardar.'),
  })

  const remove = useMutation({
    mutationFn: () => deleteMaintenance(schedule.id),
    onSuccess: refresh,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'No se pudo eliminar.'),
  })

  if (editing) {
    return (
      <div className="space-y-2 px-4 py-3">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-11 w-full rounded-lg border border-input bg-card px-3 text-sm"
          aria-label="Nombre del mantenimiento"
        />
        <div className="flex flex-wrap gap-2">
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="h-11 rounded-lg border border-input bg-card px-3 text-sm"
            aria-label="Próximo vencimiento por fecha"
          />
          <input
            type="number"
            inputMode="decimal"
            value={dueOdo}
            onChange={(e) => setDueOdo(e.target.value)}
            placeholder="Próximo por odómetro"
            className="h-11 min-w-0 flex-1 rounded-lg border border-input bg-card px-3 text-sm"
            aria-label="Próximo vencimiento por odómetro"
          />
        </div>
        <div className="flex gap-2">
          <Button type="button" className="flex-1" loading={save.isPending} onClick={() => save.mutate()}>
            Guardar
          </Button>
          <Button type="button" variant="outline" onClick={() => setEditing(false)}>
            Cancelar
          </Button>
        </div>
        {error && (
          <p role="alert" className="text-xs font-medium text-danger">
            {error}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="px-4 py-3">
      <div className="flex min-h-[56px] items-center justify-between gap-3">
        <span className="min-w-0">
          <span className="block text-sm font-medium">{schedule.maintenanceName}</span>
          <span className="block text-xs text-muted-foreground">
            {schedule.nextDueDate ? `Vence ${schedule.nextDueDate}` : 'Sin fecha'}
            {schedule.nextDueOdometer != null
              ? ` · ${schedule.nextDueOdometer.toLocaleString()}`
              : ''}
          </span>
        </span>
        <span className="flex items-center gap-2">
          <StatusPill status={schedule.dueState} />
          <RowActions
            label={`el plan ${schedule.maintenanceName}`}
            deleting={remove.isPending}
            onEdit={() => setEditing(true)}
            onDelete={() => remove.mutate()}
          />
        </span>
      </div>
      {error && (
        <p role="alert" className="mt-1 text-xs font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  )
}
